import type { TFunction } from 'i18next';

const TERMINATION_REASON_TEXT_KEY_BY_VALUE = {
  'voicemail_detection tool was called.': 'voicemail_detection_tool_was_called',
  'end_call tool was called.': 'end_call_tool_was_called',
  'All LLMs have failed': 'all_llms_have_failed',
  'Conversation has exceeded maximum duration': 'conversation_has_exceeded_maximum_duration',
  'Call was transferred to number': 'call_was_transferred_to_number',
  "Missing required dynamic variables in first message: {'primero_nome_socio'}":
    'missing_required_dynamic_variables_in_first_message',
  'Voice activity detection failed': 'voice_activity_detection_failed',
  'Generating the LLM response took too long.': 'generating_the_llm_response_took_too_long',
} as const;

const CLIENT_DISCONNECTED_PREFIX = 'Client disconnected:';

function toTitleCase(value: string) {
  const spaced = value
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .trim();
  return spaced
    .split(/\s+/)
    .map((segment) => {
      if (!segment) return '';
      return `${segment[0].toUpperCase()}${segment.slice(1).toLowerCase()}`;
    })
    .join(' ');
}

function translateTerminationReasonCode(t: TFunction, reasonCode: string): string | null {
  const normalizedReasonCode = String(reasonCode || '').trim();
  if (!normalizedReasonCode) {
    return null;
  }
  const translated = t(
    `components:callDetails.terminationReasonCodes.${normalizedReasonCode}`,
    '',
  );
  if (translated) {
    return translated;
  }
  return null;
}

function translateTerminationReasonText(t: TFunction, reasonText: string): string | null {
  const normalizedReasonText = String(reasonText || '').trim();
  if (!normalizedReasonText) {
    return null;
  }
  if (normalizedReasonText.startsWith(CLIENT_DISCONNECTED_PREFIX)) {
    return translateTerminationReasonCode(t, 'remote_party_hangup');
  }
  const translationKey = TERMINATION_REASON_TEXT_KEY_BY_VALUE[
    normalizedReasonText as keyof typeof TERMINATION_REASON_TEXT_KEY_BY_VALUE
  ];
  if (!translationKey) {
    return null;
  }
  return t(
    `components:callDetails.terminationReasonTexts.${translationKey}`,
    normalizedReasonText,
  );
}

/**
 * Resolve one termination reason into the user-facing localized label.
 *
 * We prefer the persisted canonical code when it exists, but we also translate
 * known historical/legacy codes and raw provider texts that may still be
 * present in older rows or Reverse IVR finalization paths.
 */
export function getTerminationReasonDisplay(
  t: TFunction,
  reasonCode: string | null | undefined,
  reasonText: string | null | undefined,
): string {
  const normalizedReasonCode = String(reasonCode || '').trim();
  const normalizedReasonText = String(reasonText || '').trim();

  if (normalizedReasonCode === 'manual_termination' && normalizedReasonText) {
    return normalizedReasonText;
  }

  const translatedCode = translateTerminationReasonCode(t, normalizedReasonCode);
  if (translatedCode) {
    return translatedCode;
  }

  const translatedText = translateTerminationReasonText(t, normalizedReasonText);
  if (translatedText) {
    return translatedText;
  }

  if (normalizedReasonText) {
    return normalizedReasonText;
  }
  if (normalizedReasonCode) {
    return toTitleCase(normalizedReasonCode);
  }
  return '-';
}
